/**
 * Step1PropertyInfo — Phase 27 (was "Step 2" pre-refactor).
 *
 * Address, country/state, owned-property card, autofill, regulations alert,
 * rental strategy, property type, units, beds, baths, sqft, asking price,
 * market suggestion, counter-offer, description, autofill hint.
 *
 * Verbatim port from DealWizard.jsx renderStep step === 2 branch.
 * Preserve as-is: the OwnedCard wraps its 4 sub-fields in a transition card
 * that flips border colour on toggle — every label, hint, and testid is
 * load-bearing for the e2e tests.
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar, Briefcase, Home } from "lucide-react";
import { PROPERTY_TYPE_LABELS } from "@/lib/format";
import CountryPicker, { StatePicker } from "@/components/CountryPicker";
import { GlobalLocationFields } from "@/components/GlobalLocationFields";
import MarketSuggestion from "@/components/market/MarketSuggestion";
import SingleAddressInput from "@/components/SingleAddressInput";
// Phase 47.2 — Live PSF/PSM preview chip beneath the asking-price input.
import { formatPsfPsm } from "@/lib/priceNormalisation";
import DealTypePill from "@/components/deal/DealTypePill";
import { Loader2, MapPin, Pencil, Sparkles, Wand2, AlertTriangle, Info } from "lucide-react";
import { NumberField, RegulationsAlertCard, RENTAL_STRATEGIES, SQFT_PER_SQM } from "./_shared";
import ListingContactSection from "./ListingContactSection";
import CurrencyConfigSection from "@/components/wizard/CurrencyConfigSection";
import api from "@/lib/api";
import { toast } from "sonner";
import { useEffect, useMemo, useRef } from "react";
import { parseAddress } from "@/utils/address";

export default function Step1PropertyInfo({ wizard }) {
  const {
    form, errors, dealType, addressRaw, applyParsedAddress, setProp, setAnalysis,
    setStep, onCountryChange,
    autofillLoading, autofillUndo, autofillFromMarket, undoAutofill,
    regulationsData, regulationsLoading, autofillHint,
    unitSystem, t,
    // Phase 36 — buyer/seller analysis role for this deal.
    analysisRole, setAnalysisRole,
  } = wizard;

  // ─── Phase 36 Batch AD — Address validation alert ────────────────
  // Real-time read of "is the address complete enough for autofill?".
  // Memo so the alert / button state doesn't flicker between keystrokes
  // — we only invalidate when the actual property fields change.
  const isUS = (form.property.country || "US").toUpperCase() === "US";
  const addressValidation = useMemo(() => {
    if (!isUS) {
      return { isComplete: true, missing: [], zipMismatch: null };
    }
    const parsed = parseAddress(addressRaw || "");
    const missing = [];
    if (!form.property.address?.trim()) missing.push("Street address");
    if (!form.property.city?.trim()) missing.push("City");
    // Both the legacy `state` and the new `region_code` are populated
    // in parallel — accept either as "present".
    const stateVal = (form.property.state || form.property.region_code || "").trim();
    if (!stateVal) missing.push("State");
    const zipRaw = (form.property.zip || form.property.postal_code || "").trim();
    if (!/^\d{5}$/.test(zipRaw)) missing.push("ZIP code");
    // ZIP-vs-address-text mismatch: both present, both 5-digit, but
    // different. Tells the user to reconcile before autofill.
    let zipMismatch = null;
    if (zipRaw && parsed.zip && zipRaw !== parsed.zip) {
      zipMismatch = { form: zipRaw, parsed: parsed.zip };
    }
    return {
      isComplete: missing.length === 0 && !zipMismatch,
      missing,
      zipMismatch,
    };
  }, [
    isUS,
    addressRaw,
    form.property.address,
    form.property.city,
    form.property.state,
    form.property.region_code,
    form.property.zip,
    form.property.postal_code,
  ]);

  // PostHog: fire once per (deal × first-time-incomplete-shown). We
  // dedupe via a ref so re-renders don't double-count, and we capture
  // the actual missing-fields list so the dashboard can tell which
  // field most commonly trips users up.
  const warnedRef = useRef(false);
  useEffect(() => {
    if (warnedRef.current) return;
    if (addressValidation.isComplete) return;
    if (!isUS) return;
    warnedRef.current = true;
    try {
      window.posthog?.capture?.("address_validation_warning_shown", {
        missing_fields: addressValidation.missing,
        zip_mismatch: Boolean(addressValidation.zipMismatch),
      });
    } catch {
      /* posthog blocked — ignore */
    }
  }, [addressValidation.isComplete, addressValidation.missing, addressValidation.zipMismatch, isUS]);

  // Anchor we scroll the user to when they click Autofill but the
  // form isn't ready. ScrollIntoView is gentler than a hard toast.
  const alertRef = useRef(null);
  const handleAutofillClick = () => {
    if (!addressValidation.isComplete) {
      try {
        alertRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        /* older browsers — no-op */
      }
      const parts = [];
      if (addressValidation.missing.length) {
        parts.push(`Missing: ${addressValidation.missing.join(", ")}`);
      }
      if (addressValidation.zipMismatch) {
        parts.push(
          `ZIP ${addressValidation.zipMismatch.form} doesn't match address (${addressValidation.zipMismatch.parsed})`
        );
      }
      toast.error(parts.join(" · ") || "Address incomplete — fix the highlighted fields.");
      return;
    }
    autofillFromMarket?.();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Tell us about the property</h2>
          <p className="mt-1 text-sm text-slate-500">Address + asset basics.</p>
        </div>
        <button
          type="button"
          onClick={() => setStep(1)}
          data-testid="wizard-change-deal-type"
          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
        >
          <Pencil className="h-3 w-3" /> Change
        </button>
      </div>
      {dealType ? <DealTypePill type={dealType} size="lg" testid="wizard-current-type" /> : null}

      {/* Phase 36 — buyer/seller analysis role toggle. Pre-filled from
          the user's default; per-deal override saved on submit. */}
      <div
        data-testid="wizard-role-toggle"
        className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
      >
        <div className="mb-3">
          <p className="text-sm font-semibold text-slate-900">Analyze this deal as</p>
          <p className="text-xs text-slate-500">
            Buyer math anchors to your offer / counter; seller math anchors to the asking price.
          </p>
        </div>
        <div role="radiogroup" aria-label="Analysis role" className="grid gap-3 sm:grid-cols-2">
          {[
            {
              value: "buyer",
              label: "Investor (Buyer side)",
              Icon: Briefcase,
              tone: "indigo",
            },
            {
              value: "seller",
              label: "Owner (Seller side)",
              Icon: Home,
              tone: "amber",
            },
          ].map((opt) => {
            const active = analysisRole === opt.value;
            const ring = active
              ? opt.tone === "amber"
                ? "ring-2 ring-amber-400 bg-amber-50/50 border-amber-200"
                : "ring-2 ring-indigo-400 bg-indigo-50/50 border-indigo-200"
              : "border-slate-200 hover:border-slate-300";
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`wizard-role-${opt.value}`}
                onClick={() => setAnalysisRole(opt.value)}
                className={`flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-left transition-all ${ring}`}
              >
                <opt.Icon
                  className={`h-4 w-4 flex-shrink-0 ${
                    opt.tone === "amber" ? "text-amber-600" : "text-indigo-600"
                  }`}
                />
                <span className="text-sm font-medium text-slate-900">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Phase 19 — "I already own this property" toggle. When ON, reveals
         4 fields for purchase history + persists for the historical-perf
         tab. The toggle position persists across sessions via localStorage. */}
      <div
        className={`rounded-2xl border-2 p-5 transition-colors ${
          form.analysis.is_owned
            ? "border-indigo-300 bg-indigo-50/60"
            : "border-slate-200 bg-white"
        }`}
        data-testid="wizard-owned-card"
        data-state={form.analysis.is_owned ? "on" : "off"}
      >
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.analysis.is_owned}
            onChange={(e) => {
              const next = e.target.checked;
              setAnalysis("is_owned", next);
              try { window.localStorage.setItem("runidea.wizard.last_is_owned", next ? "1" : "0"); } catch (e) {
                // Phase 36 Batch 1 — log instead of silent swallow.
                console.warn("[Step1PropertyInfo] persist last_is_owned failed:", e);
              }
            }}
            data-testid="wizard-is-owned-toggle"
            className="mt-0.5 h-5 w-5 cursor-pointer rounded border-slate-300 accent-indigo-600"
            aria-label="I already own this property"
          />
          <div className="flex-1">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Calendar className="h-4 w-4 text-indigo-600" /> I already own this property
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              Turn this on if you bought this place years ago — we'll unlock the
              Investment Performance tab with appreciation, equity, IRR, and an
              S&amp;P 500 comparison.
            </p>
          </div>
        </label>
        {form.analysis.is_owned ? (
          <div
            className="mt-4 grid gap-4 sm:grid-cols-2"
            data-testid="wizard-owned-fields"
          >
            <div>
              <Label className="text-slate-700">Purchase year</Label>
              <Input
                type="number"
                min={1950}
                max={new Date().getFullYear()}
                placeholder="e.g. 2018"
                value={form.analysis.purchase_year ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setAnalysis("purchase_year", v ? Number(v) : null);
                }}
                data-testid="wizard-purchase-year"
                className="mt-1.5"
              />
            </div>
            <NumberField
              id="purchase-price-paid"
              label="Purchase price"
              prefix="$"
              value={form.analysis.purchase_price_paid}
              onChange={(v) => setAnalysis("purchase_price_paid", v)}
              testid="wizard-purchase-price-paid"
            />
            <NumberField
              id="orig-loan"
              label="Original loan amount"
              prefix="$"
              hint="Skip if you bought all-cash"
              value={form.analysis.original_loan_amount}
              onChange={(v) => setAnalysis("original_loan_amount", v)}
              testid="wizard-original-loan-amount"
            />
            <NumberField
              id="orig-rate"
              label="Original interest rate"
              suffix="%"
              step={0.125}
              hint="From your original mortgage"
              value={form.analysis.original_interest_rate}
              onChange={(v) => setAnalysis("original_interest_rate", v)}
              testid="wizard-original-interest-rate"
            />
            {/* Batch F — Realized Performance fields */}
            <div>
              <Label className="text-slate-700">Rental start year</Label>
              <Input
                type="number"
                min={1950}
                max={new Date().getFullYear()}
                placeholder={form.analysis.purchase_year ?? "e.g. 2018"}
                value={form.analysis.rental_start_year ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setAnalysis("rental_start_year", v ? Number(v) : null);
                }}
                data-testid="wizard-rental-start-year"
                className="mt-1.5"
              />
              <p className="mt-1 text-xs text-slate-500">Defaults to purchase year if blank.</p>
            </div>
            <NumberField
              id="rent-growth"
              label="Annual rent growth"
              suffix="%"
              step={0.1}
              hint="Used to estimate historical rent. We back-calculate past rent from today's rent."
              value={form.analysis.annual_rent_growth_percent}
              onChange={(v) => setAnalysis("annual_rent_growth_percent", v)}
              testid="wizard-annual-rent-growth-percent"
            />
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700">Current estimated value</Label>
                <button
                  type="button"
                  data-testid="wizard-ai-value-estimate-btn"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/properties/estimate-value", {
                        address: form.property.address,
                        city: form.property.city,
                        state: form.property.state,
                        zip: form.property.zip,
                        country: form.property.country,
                        property_type: form.property.property_type,
                        bedrooms: form.property.bedrooms,
                        bathrooms: form.property.bathrooms,
                        square_feet: form.property.square_feet,
                        original_purchase_price: form.analysis.purchase_price_paid,
                        purchase_year: form.analysis.purchase_year,
                      });
                      if (data?.estimate) {
                        setAnalysis("current_estimated_value", Number(data.estimate));
                        setAnalysis("current_value_source", "ai_estimate");
                        setAnalysis("current_value_updated_at", new Date().toISOString());
                        const conf = data.confidence ? ` · ${data.confidence} confidence` : "";
                        toast.success(`AI suggests $${Math.round(data.estimate).toLocaleString()}${conf}`);
                      } else {
                        toast.error("AI couldn't estimate — enter a value manually.");
                      }
                    } catch (err) {
                      toast.error("Estimate failed. Try again or enter a value manually.");
                    }
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100"
                >
                  <Wand2 className="h-3 w-3" />
                  AI estimate
                </button>
              </div>
              <div className="mt-1.5">
                <NumberField
                  id="current-estimated-value"
                  label=""
                  prefix="$"
                  hint={
                    form.analysis.current_value_source === "ai_estimate"
                      ? "From AI estimate. You can override."
                      : "Today's market value."
                  }
                  value={form.analysis.current_estimated_value}
                  onChange={(v) => {
                    setAnalysis("current_estimated_value", v);
                    setAnalysis("current_value_source", "manual");
                  }}
                  testid="wizard-current-estimated-value"
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="grid gap-5 sm:grid-cols-6">
        {/* Phase 36 Batch R — Country + region + postal triad as a
            single coordinated picker. Backed by /api/locations/*.
            The old CountryPicker (limited to countries with tax
            engines) is still used as a sidecar by `onCountryChange`
            to set tax defaults — but for the address fields, this
            global picker is authoritative. */}
        <div className="sm:col-span-6">
          <GlobalLocationFields
            country={form.property.country_iso2 || form.property.country || "US"}
            region={form.property.region_code || form.property.state || ""}
            postal={form.property.postal_code || form.property.zip || ""}
            onCountryChange={(iso2) => {
              setProp("country_iso2", iso2);
              setProp("country", iso2);
              // Region from the previous country is almost never valid
              // in the new country — clear it so the user has to
              // re-pick (validated server-side anyway, but the UI
              // should feel clean).
              setProp("region_code", "");
              setProp("state", "");
              // Trigger the tax-defaults side-effect the old picker did.
              onCountryChange(iso2);
            }}
            onRegionChange={(code) => {
              setProp("region_code", code);
              setProp("state", code);
            }}
            onPostalChange={(v) => {
              setProp("postal_code", v);
              setProp("zip", v);
            }}
          />
        </div>

        <div className="sm:col-span-6">
          <SingleAddressInput
            value={addressRaw}
            onChange={applyParsedAddress}
            error={errors.address}
            testid="prop-address-full"
          />
          {(form.property.country || "US").toUpperCase() === "US" && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAutofillClick}
                disabled={autofillLoading}
                data-testid="wizard-autofill-market"
                className="border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
              >
                {autofillLoading ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                )}
                Autofill from market data
              </Button>
              {autofillUndo ? (
                <button
                  type="button"
                  onClick={undoAutofill}
                  data-testid="wizard-autofill-undo"
                  className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 underline-offset-2 hover:underline"
                >
                  Undo autofill
                </button>
              ) : (
                <span className="text-xs text-slate-500">
                  Pulls beds, baths, sqft, year built &amp; market value/rent from RentCast + ATTOM.
                </span>
              )}
            </div>
          )}
          {/* Phase 36 Batch AD — Address Validation Alert.
              Only renders for US deals while the address is incomplete
              or has a ZIP-vs-address-text mismatch. Lists exactly
              what's missing + a one-line helpful tip. Doesn't block
              the wizard — the user can still Next or edit manually. */}
          {isUS && !addressValidation.isComplete && (
            <div
              ref={alertRef}
              data-testid="wizard-address-validation-alert"
              data-zip-mismatch={addressValidation.zipMismatch ? "true" : "false"}
              role="status"
              aria-live="polite"
              className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                {addressValidation.missing.length > 0 ? (
                  <p
                    data-testid="wizard-address-validation-missing"
                    className="font-medium"
                  >
                    Missing: {addressValidation.missing.join(", ")}
                  </p>
                ) : null}
                {addressValidation.zipMismatch ? (
                  <p
                    data-testid="wizard-address-validation-mismatch"
                    className="font-medium"
                  >
                    ZIP code ({addressValidation.zipMismatch.form}) doesn&apos;t match the ZIP in the address ({addressValidation.zipMismatch.parsed}). Reconcile before autofilling.
                  </p>
                ) : null}
                <p className="mt-1 flex items-start gap-1 text-xs text-amber-800">
                  <Info className="mt-0.5 h-3 w-3 flex-shrink-0" />
                  <span>
                    Tip: type the full address in the Property Address field
                    (e.g. &ldquo;123 Main St, Miami, FL 33143&rdquo;) and
                    we&apos;ll fill State &amp; ZIP for you.
                  </span>
                </p>
              </div>
            </div>
          )}
          {/* Phase 17 — Local Regulations Alert (AI-summarised, cached 7d). */}
          <RegulationsAlertCard
            city={form.property.city}
            state={form.property.state}
            strategy={form.analysis.rental_strategy || "long_term"}
            data={regulationsData}
            loading={regulationsLoading}
          />
        </div>
        {/* Phase 17 — Rental Strategy segmented control. Drives the
           regulations lookup above (short-term has very different rules
           than a 12-month lease) and is persisted on the deal.
           Phase 27.0.1 — Gated to dealType === "rental" only. Pre-refactor
           this rendered for every deal type which leaked into FLIP / BRRRR /
           Multifamily wizards; the tester correctly flagged that as noise. */}
        {dealType === "rental" ? (
        <div className="sm:col-span-6">
          <Label className="text-slate-700">Rental strategy</Label>
          <div
            role="radiogroup"
            aria-label="Rental strategy"
            data-testid="wizard-rental-strategy"
            className="mt-1.5 inline-flex w-full rounded-xl border border-slate-200 bg-slate-50 p-1 sm:w-auto"
          >
            {RENTAL_STRATEGIES.map((opt) => {
              const active = (form.analysis.rental_strategy || "long_term") === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setAnalysis("rental_strategy", opt.id)}
                  data-testid={`rental-strategy-${opt.id}`}
                  data-active={active ? "true" : "false"}
                  className={`flex-1 rounded-lg px-4 py-1.5 text-sm font-medium transition-all sm:flex-none ${
                    active
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {RENTAL_STRATEGIES.find((o) => o.id === (form.analysis.rental_strategy || "long_term"))?.hint}
          </p>
        </div>
        ) : null}
        <div className="sm:col-span-3">
          <Label className="text-slate-700">Property type</Label>
          <Select value={form.property.property_type} onValueChange={(v) => setProp("property_type", v)}>
            <SelectTrigger data-testid="prop-type" className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PROPERTY_TYPE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={`prop-type-${k}`}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-3">
          <NumberField id="units" label="Units" value={form.property.units}
            onChange={(v) => setProp("units", v)} testid="prop-units" min={1} />
        </div>
        <div className="sm:col-span-2">
          <NumberField id="bedrooms" label="Bedrooms" value={form.property.bedrooms}
            onChange={(v) => setProp("bedrooms", v)} testid="prop-bedrooms" />
        </div>
        <div className="sm:col-span-2">
          <NumberField id="bathrooms" label="Bathrooms" value={form.property.bathrooms}
            onChange={(v) => setProp("bathrooms", v)} testid="prop-bathrooms" />
        </div>
        <div className="sm:col-span-2">
          <NumberField
            id="sqft"
            label={unitSystem === "metric" ? "Area (m²)" : "Area (ft²)"}
            value={
              unitSystem === "metric"
                ? Math.round((form.property.square_feet || 0) / SQFT_PER_SQM)
                : form.property.square_feet
            }
            onChange={(v) => {
              const sqft = unitSystem === "metric" ? Number(v) * SQFT_PER_SQM : Number(v);
              setProp("square_feet", Math.round(sqft * 100) / 100);
            }}
            testid="prop-sqft"
            hint={unitSystem === "metric" ? "Stored as ft² for the engine." : null}
          />
        </div>
        <div className="sm:col-span-6">
          <NumberField id="price" label="Asking price" prefix="$"
            value={form.property.asking_price} onChange={(v) => setProp("asking_price", v)}
            testid="prop-price" hint="Listed price from the seller. Used as a comparison anchor — the analysis anchors on your counter-offer below when set." />
          {errors.asking_price && <p className="mt-1 text-xs text-red-600">{errors.asking_price}</p>}
          {/* Phase 47.2 — Live PSF/PSM chip beneath the asking-price
              input. Tilde-prefixed to convey "preview from your current
              inputs", muted slate so it doesn't compete with the headline
              dollar number. Renders "—" if sqft missing/invalid. */}
          {Number(form.property.asking_price) > 0 && Number(form.property.square_feet) >= 50 ? (
            <div
              data-testid="wizard-asking-psf-psm"
              className="mt-1 text-[11px] tabular-nums text-slate-500"
            >
              ~{formatPsfPsm(form.property.asking_price, form.property.square_feet)}
            </div>
          ) : null}
          <MarketSuggestion
            kind="value"
            address={form.property.address}
            city={form.property.city}
            state={form.property.state}
            zip={form.property.zip}
            country={form.property.country}
            beds={Number(form.property.bedrooms) || undefined}
            baths={Number(form.property.bathrooms) || undefined}
            sqft={Number(form.property.square_feet) || undefined}
            propertyType={form.property.property_type}
            onAccept={(v) => setProp("asking_price", v)}
            testidPrefix="wizard-market-value"
          />
        </div>

        {/* Phase 15 — Counter-offer + description (optional, but the
           Scenarios tab uses them). Inline so they appear right after the
           asking price for clear price-anchor → negotiation flow. */}
        <div className="sm:col-span-3">
          <NumberField
            id="counter_offer_price"
            label="Counter-offer (optional)"
            prefix="$"
            value={form.analysis.counter_offer_price}
            onChange={(v) => setAnalysis("counter_offer_price", v)}
            testid="wizard-counter-offer"
            hint="What you're offering. Becomes the basis of all analysis when set — asking is shown for comparison only."
          />
        </div>
        <div className="sm:col-span-6">
          <label htmlFor="prop-description" className="text-sm font-medium text-slate-700">
            Property notes (optional)
          </label>
          <textarea
            id="prop-description"
            data-testid="wizard-description"
            value={form.analysis.description || ""}
            onChange={(e) => setAnalysis("description", e.target.value.slice(0, 1000))}
            maxLength={1000}
            rows={3}
            placeholder="Condition, deferred maintenance, seller motivation clues, anything the AI should know…"
            className="mt-1 block w-full rounded-md border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
          />
          <p className="mt-1 text-[10px] text-slate-400">
            {(form.analysis.description || "").length} / 1000 — fed into AI Analyst + Negotiation prompts.
          </p>
        </div>
        {autofillHint && form.property.asking_price > 0 && (
          <div className="sm:col-span-6">
            <div
              data-testid="wizard-autofill-hint"
              className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-700"
            >
              <Wand2 className="h-3.5 w-3.5" />
              {t("dealDetail.country.tax_hint", { country: autofillHint.country_name })}
            </div>
          </div>
        )}
      </div>

      {/* Phase 36 Batch X — GMIA "Step 1.5" Currency configuration.
          Renders compactly when property currency == base currency,
          and as a full card with YES/NO branch when they differ. */}
      <CurrencyConfigSection wizard={wizard} />

      {/* Phase 36 — Listing & contact info section, hanging off the same
          step the user already touched for property details. */}
      <ListingContactSection wizard={wizard} />
    </div>
  );
}
